import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { apiCall } from '../services/api';

const PaymentRedirectHandler = ({ user }) => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user) {
      // Not logged in, redirect to login
      navigate('/login', { replace: true });
      return;
    }
    
    // Parse the URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const razorpayPaymentId = queryParams.get('razorpay_payment_id');
    const razorpaySubscriptionId = queryParams.get('razorpay_subscription_id');
    const razorpaySignature = queryParams.get('razorpay_signature');
    const razorpayError = queryParams.get('error_code') || queryParams.get('error');
    
    // PayPal parameters
    const paypalToken = queryParams.get('token');
    const paypalPayerId = queryParams.get('PayerID');
    
    // Check for success or failure
    if (razorpayPaymentId && razorpaySubscriptionId && razorpaySignature) {
      // Razorpay success - payment was completed
      setStatus('success');
      setMessage('Your payment was successful! Your subscription has been activated.');
      
      // Verify the payment on the server
      verifyRazorpayPayment(razorpayPaymentId, razorpaySubscriptionId, razorpaySignature);
    } else if (paypalToken && paypalPayerId) {
      // PayPal success
      setStatus('success');
      setMessage('Your PayPal payment was successful! Your subscription has been activated.');
      
      // Verify PayPal payment
      verifyPayPalPayment(paypalToken, paypalPayerId);
    } else if (razorpayError) {
      // Failed payment
      setStatus('error');
      setMessage(`Payment failed: ${razorpayError}`);
      
      // Log the failure on the server
      logPaymentFailure(razorpayError, queryParams.get('error_description') || '');
    } else {
      // No recognizable parameters, probably a direct visit
      setStatus('error');
      setMessage('Invalid payment redirect. Please try subscribing again.');
    }
    
    // Redirect to main app after a delay
    const timer = setTimeout(() => {
      navigate('/subscription-dashboard', { replace: true });
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [location, navigate, user]);

  const verifyRazorpayPayment = async (paymentId, subscriptionId, signature) => {
    try {
      const response = await apiCall('/api/subscriptions/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_payment_id: paymentId,
          razorpay_subscription_id: subscriptionId,
          razorpay_signature: signature,
          user_id: user?.uid,
        }),
      });
      
      if (!response.ok) {
        console.error('Error verifying payment');
        setStatus('error');
        setMessage('There was an issue verifying your payment. Please contact support.');
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
    }
  };
  
  const verifyPayPalPayment = async (token, payerId) => {
    try {
      const response = await apiCall('/api/subscriptions/verify-paypal-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          payer_id: payerId,
          user_id: user?.uid,
        }),
      });
      
      if (!response.ok) {
        console.error('Error verifying PayPal payment');
        setStatus('error');
        setMessage('There was an issue verifying your PayPal payment. Please contact support.');
      }
    } catch (error) {
      console.error('Failed to verify PayPal payment:', error);
    }
  };
  
  const logPaymentFailure = async (errorCode, errorDescription) => {
    try {
      await apiCall('/api/subscriptions/log-payment-failure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          error_code: errorCode,
          error_description: errorDescription,
          user_id: user?.uid,
        }),
      });
    } catch (error) {
      console.error('Failed to log payment failure:', error);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0c1526] to-[#142039] flex items-center justify-center">
      <div className="bg-[#1e293b] rounded-xl shadow-xl p-8 max-w-md w-full border border-gray-700">
        {status === 'processing' && (
          <div className="text-center">
            <Loader className="h-16 w-16 text-[#ff7e00] mx-auto animate-spin mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Processing your subscription...</h2>
            <p className="text-gray-300">Please wait while we verify your payment.</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Subscription Activated!</h2>
            <p className="text-gray-300 mb-4">{message}</p>
            <p className="text-gray-400 text-sm">Redirecting you to the app...</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-gray-300 mb-4">{message}</p>
            <p className="text-gray-400 text-sm">Redirecting you to the app...</p>
            <button
              onClick={() => navigate('/subscription-dashboard', { replace: true })}
              className="mt-6 px-4 py-2 bg-[#273349] text-white rounded-lg hover:bg-[#324056] transition-colors"
            >
              Continue to App
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRedirectHandler;